import baseApi from "@/lib/redux/api/baseApi";

export const clientApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Add Client
    addClient: builder.mutation({
      query: (clientData) => ({
        url: "/client/add",
        method: "POST",
        body: clientData,
      }),
      providesTags: ["Clients"],
    }),

    // Edit Client
    editClient: builder.mutation({
      query: ({ id, clientData }) => ({
        url: `/client/edit/${id}`,
        method: "POST",
        body: clientData,
      }),
      providesTags: ["Clients"],
    }),

    // View Client
    viewClient: builder.query({
      query: (id) => ({
        url: `/client/view/${id}`,
        method: "GET",
      }),
      providesTags: ["Clients"],
    }),

    // Check Active Membership
    checkActiveMembership: builder.query({
      query: (clientGuid) => ({
        url: `/client/check/active-membership/${clientGuid}`,
        method: "GET",
      }),
      providesTags: ["Clients"],
    }),

    changeClientMembership: builder.mutation({
      query: ({ clientGuid, membershipGuid }) => ({
        url: `/client/membership/change/${clientGuid}`,
        method: "POST",
        body: { membership_guid: membershipGuid },
      }),
      invalidatesTags: ["Clients"],
    }),

    removeClientMembership: builder.mutation({
      query: (clientGuid) => ({
        url: `/client/membership/${clientGuid}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Clients"],
    }),

    // View Client List
    viewClientList: builder.query({
      query: (userId) => ({
        url: `/client/view/list/${userId}`,
        method: "GET",
      }),
      providesTags: ["Clients"],
    }),

    // Delete Client
    deleteClient: builder.mutation({
      query: (id) => ({
        url: `/client/delete/${id}`,
        method: "DELETE",
      }),
      providesTags: ["Clients"],
    }),

    // Delete Child
    deleteChild: builder.mutation({
      query: (guid) => ({
        url: `/client/delete_child/${guid}`,
        method: "DELETE",
      }),
      providesTags: ["Clients"],
    }),

    // Delete Child Package
    deleteChildPackage: builder.mutation({
      query: (guid) => ({
        url: `/client/delete_child_package/${guid}`,
        method: "DELETE",
      }),
      providesTags: ["Clients"],
    }),
    // Delete Child classes
    deleteChildClasses: builder.mutation({
      query: (guid) => ({
        url: `/client/delete/booking/session/${guid}`,
        method: "DELETE",
      }),
      providesTags: ["Clients"],
    }),
    // Assign Package
    assignPackage: builder.mutation({
      query: ({ guid, packageData }) => ({
        url: `/client/assign/package/${guid}`,
        method: "POST",
        body: packageData,
      }),
      invalidatesTags: ["Dashboard", "Clients"],
    }),

    // Delete Child Image
    deleteChildImage: builder.mutation({
      query: (guid) => ({
        url: `/client/delete_child_image/${guid}`,
        method: "DELETE",
      }),
      providesTags: ["Clients"],
    }),
    deleteClientImage: builder.mutation({
      query: (guid) => ({
        url: `/client/delete_client_image/${guid}`,
        method: "DELETE",
      }),
      providesTags: ["Clients"],
    }),
    getClassesList: builder.mutation({
      query: ({ selected_day, selected_date }) => ({
        url: `/client/detail/${selected_day}`,
        method: "POST",
        body: { selected_date },
      }),
    }),
    assignClassesToChild: builder.mutation({
      query: ({ child_guid, classesData }) => ({
        url: `/booking/session/${child_guid}`,
        method: "POST",
        body: classesData,
      }),
    }),
    uploadClientDocument: builder.mutation({
      query: (documentData) => ({
        url: "/client/add/document",
        method: "POST",
        body: documentData,
      }),
    }),
    // Edit Document
    editClientDocument: builder.mutation({
      query: ({ id, documentData }) => ({
        url: `/client/edit/document/${id}`,
        method: "POST",
        body: documentData,
      }),
    }),
    deleteClientDocument: builder.mutation({
      query: ({ id }) => ({
        url: `/client/delete/document/${id}`,
        method: "DELETE",
      }),
    }),
    clientDocumentsListing: builder.query({
      query: (id) => ({
        url: `/client/view/document/list/${id}`,
        method: "GET",
      }),
      providesTags: ["Documents"],
    }),
    // Add Client note
    addClientNote: builder.mutation({
      query: (noteData) => ({
        url: "/client-note/add",
        method: "POST",
        body: noteData,
      }),
      invalidatesTags: ["Clients", "Dashboard"],
    }),
    // fetch Client note
    clientNotesListing: builder.query({
      query: (guid) => ({
        url: `/client-note/view/${guid}`,
        method: "GET",
      }),
      providesTags: ["Documents"],
    }),
    // Delete client note //
    deleteClientNote: builder.mutation({
      query: (guid) => ({
        url: `/client-note/delete/${guid}`,
        method: "DELETE",
      }),
      providesTags: ["Clients"],
    }),
    // Add this to your existing clientsApi.js endpoints
    updateChildPackage: builder.mutation({
      query: (data) => ({
        url: `/client/assign/package/update/${data.guid}`,
        method: "POST",
        body: {
          package_guid: data.package_guid,
          price: data.price,
          total_session: data.total_session,
          expiry_date: data.expiry_date,
          discount_id: data.discount_id ?? null,
          discount_note: data.discount_note ?? null,
        },
      }),
      invalidatesTags: ["Client", "Dashboard"],
    }),

    getClientLocations: builder.query({
      query: (guid) => ({
        url: `/client/locations/${guid}`,
        method: "GET",
      }),
      providesTags: ["Clients"],
    }),

    assignClientLocations: builder.mutation({
      query: ({ guid, location_guids }) => ({
        url: `/client/assign/locations/${guid}`,
        method: "POST",
        body: { location_guids },
      }),
      invalidatesTags: ["Clients"],
    }),

  }),
  overrideExisting: true,
});

// Export hooks
export const {
  useAddClientMutation,
  useEditClientMutation,
  useViewClientQuery,
  useViewClientListQuery,
  useDeleteClientMutation,
  useDeleteChildMutation,
  useDeleteChildClassesMutation,
  useDeleteChildPackageMutation,
  useDeleteChildImageMutation,
  useDeleteClientImageMutation,
  useAssignPackageMutation,
  useGetClassesListMutation,
  useAssignClassesToChildMutation,
  useUploadClientDocumentMutation,
  useEditClientDocumentMutation,
  useDeleteClientDocumentMutation,
  useClientDocumentsListingQuery,
  useAddClientNoteMutation,
  useClientNotesListingQuery,
  useDeleteClientNoteMutation,
  useUpdateChildPackageMutation,
  useCheckActiveMembershipQuery,
  useChangeClientMembershipMutation,
  useRemoveClientMembershipMutation,
  useGetClientLocationsQuery,
  useAssignClientLocationsMutation,
} = clientApi;
